/*
  Saint Véra · sacola (gaveta e página /cart). Dono: agente D (estrategia/horizon-v2/CONTRATO.md).

  O que faz (tudo por delegação em [data-sv-act]; o HTML vem do Liquid e é re-renderizado pela plataforma):
  - quantidade (com debounce), remover + desfazer, trocar cor/tamanho de forma transacional
  - entrega (datas estimadas), sugestões com motivo (Recommendations API), ponte para o mini-PDP e o modal de pagamento
  - gaveta como modal também no desktop (visual de referência: sobreposição com fundo desfocado, sem espremer a página)
  - gaveta enxuta (v5): sem sugestões nem 2ª peça (só a página /cart), contagem no ícone e "N pçs" ajustadas na hora,
    foto da linha troca junto com a cor, aviso "Item removido do carrinho" no lugar do Desfazer

  Atualização de tela e contadores (header, barra inferior): toda mutação dispara o CartLinesUpdateEvent do Horizon
  (@shopify/events) com a promise resolvida SÓ depois do sucesso; cart-items-component faz o morph da seção e
  cart-icon.js atualiza o contador. Falha = promise rejeitada + re-render do servidor (estado verdadeiro).

  Contrato Yampi: o botão Finalizar (#checkout, sv-cart-summary.liquid) chama yampiClick() direto. Este arquivo só
  o segura enquanto há alteração em andamento, para a Yampi ler /cart.json já com a sacola final.
*/
import { sectionRenderer } from '@theme/section-renderer';
import { lockScroll } from '@theme/utilities';
import { removeTrapFocus } from '@theme/focus';

/* 2ª peça com 15% off (Anaju, Celine, Solene). A cobrança real é a promoção configurada na Yampi; o tema só mostra
   o mesmo desconto (card no lugar da sugestão, linha com selo e total). SV_2A_ATIVO = false desliga tudo na hora. */
const SV_SUGGEST_ATIVO = false;
const SV_2A_ATIVO = false; // 17/10/2026: promoção da 2ª peça pausada pelo dono
const SV_2A = {
  pct: 15,
  // 2ª unidade do mesmo produto
  same: ['vestido-longo-feminino-anaju', 'vestido-midi-feminino-celine'],
  // produto de uma cor só: a 2ª peça é um destes (a chave do card troca entre eles)
  pair: { 'vestido-midi-feminino-solene': ['vestido-longo-feminino-anaju', 'vestido-midi-feminino-celine'] },
  labels: {
    color: 'Gostou de outra cor também?',
    look: 'Complete o look',
    desc: '2ª peça com 15% off',
    ticket: '15% OFF',
    add: 'Adicionar',
    seg: 'Escolha a 2ª peça',
    short: { 'vestido-longo-feminino-anaju': 'Anaju', 'vestido-midi-feminino-celine': 'Celine' },
  },
};

/* sv2-math:start (puro; conferido por scratchpad/sv2-check.cjs) */
const sv2Off = (cents, pct) => Math.round((cents * (100 - pct)) / 100);
const sv2Pix = (cents, pixPct) => Math.floor((cents * (100 - pixPct) + 50) / 100);
// lines: [{key, handle, qty, price}] na ordem da sacola (a mais recente primeiro). Devolve a ÚNICA unidade com
// desconto: {key, cents} ou null.
function sv2Pick(lines, cfg) {
  const units = (h) => lines.filter((l) => l.handle === h).reduce((s, l) => s + l.qty, 0);
  let line = null;
  cfg.same.forEach((h) => {
    if (!line && units(h) >= 2) line = lines.find((l) => l.handle === h);
  });
  Object.keys(cfg.pair).forEach((base) => {
    if (!line && units(base) > 0) line = lines.find((l) => cfg.pair[base].includes(l.handle)) || null;
  });
  return line ? { key: line.key, cents: line.price - sv2Off(line.price, cfg.pct) } : null;
}
// sub = total da sacola sem a 2ª peça; disc = desconto da 2ª peça. Pix sobre o subtotal já descontado.
function sv2Totals(sub, disc, pixPct) {
  const subD = sub - disc;
  const tot = sv2Pix(subD, pixPct);
  return { subD, tot, save: sub - tot, second: disc, pix: subD - tot };
}
/* sv2-math:end */

if (!window.__svCartInit) {
  window.__svCartInit = true;
  init();
}

function init() {
  const doc = document;
  const $ = (s, r = doc) => r.querySelector(s);
  const $$ = (s, r = doc) => Array.from(r.querySelectorAll(s));
  const SVx = () => window.SV || {};
  const cfg = () => SVx().config || { routes: {}, shipping: { days: [5, 12] } };

  const S = {
    queue: Promise.resolve(),
    busy: 0,
    qty: new Map(), // key -> {target, timer}
    undo: null, // {id, quantity, properties, selling_plan, title}
    undoError: '',
    pending: new Map(), // key -> mensagem de erro a mostrar na linha depois do morph
    focusAfter: null,
    products: new Map(), // handle -> Promise<produto .js>
    suggest: new Map(), // seed -> {html, intent}
    suggestJobs: new Map(), // seed -> Promise em andamento
    events: null,
  };

  const MSG_NET = 'Sem conexão. Sua sacola não foi alterada. Tente de novo.';
  const MSG_GENERIC = 'Não foi possível atualizar a sacola. Tente de novo.';

  /* ---------------- util ---------------- */
  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const route = (k, fallback) => (cfg().routes && cfg().routes[k]) || fallback;
  const tick = (ms) => new Promise((r) => setTimeout(r, ms));

  function live(msg) {
    let el = $('#sv-cart-live');
    if (!el) {
      el = doc.createElement('div');
      el.id = 'sv-cart-live';
      el.className = 'sv-visually-hidden';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      doc.body.appendChild(el);
    }
    el.textContent = '';
    setTimeout(() => (el.textContent = msg), 30);
  }

  /* contagem de peças (número dentro do ícone e "N pçs na sacola"): o servidor desenha, aqui só o ajuste na hora */
  function paintCount(n) {
    $$('[data-sv-count]').forEach((el) => {
      const t = n > 0 ? String(n) : '';
      if (el.textContent !== t) el.textContent = t;
    });
    $$('[data-sv-qtylabel]').forEach((el) => {
      const t = n === 1 ? '1 pç na sacola' : `${n} pçs na sacola`;
      if (el.textContent !== t) el.textContent = t;
    });
  }

  /* aviso curto na gaveta (some sozinho). Fica no <dialog>, fora do trecho que o morph redesenha; nasce vazio para o leitor de tela anunciar o texto */
  let toastTimer = 0;
  function toastEl() {
    const dlg = $('#cart-drawer dialog');
    if (!dlg) return null;
    let el = $(':scope > .sv-toast', dlg);
    if (!el) {
      el = doc.createElement('div');
      el.className = 'sv-toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      dlg.appendChild(el);
    }
    return el;
  }
  function toast(msg) {
    const el = toastEl();
    if (!el) return;
    const foot = $('.sv-foot', el.parentElement);
    el.style.bottom = `${(foot ? foot.offsetHeight : 0) + 12}px`;
    el.textContent = msg;
    void el.offsetWidth; // 1ª vez: a transição precisa partir do estado escondido
    el.classList.add('is-on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-on'), 2200);
  }

  function getEvents() {
    if (!S.events) S.events = import('@shopify/events').catch(() => null);
    return S.events;
  }

  function sectionComponents() {
    return $$('cart-items-component').filter((c) => c.dataset.sectionId);
  }
  function sectionIds() {
    return Array.from(new Set(sectionComponents().map((c) => c.dataset.sectionId)));
  }

  async function cartPost(kind, payload) {
    const url = route(kind, kind === 'cartAdd' ? '/cart/add' : '/cart/change') + '.js';
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(payload),
    });
    let json = null;
    try {
      json = await res.json();
    } catch (e) {
      json = null;
    }
    const failed = !res.ok || !json || json.status >= 400 || json.errors;
    return { ok: !failed, status: res.status, json };
  }
  async function fetchCart() {
    const res = await fetch(route('cart', '/cart').replace(/\/$/, '') + '.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    if (!res.ok) throw new Error('cart_fetch_failed');
    return res.json();
  }
  function apiMessage(r) {
    const j = (r && r.json) || {};
    const m = j.description || (typeof j.errors === 'string' ? j.errors : '') || j.message;
    return m && m !== 'Cart Error' ? m : MSG_GENERIC;
  }
  class CartError extends Error {
    constructor(message, userMessage) {
      super(message);
      this.userMessage = userMessage || message;
    }
  }
  const apiError = (r) => new CartError(apiMessage(r), apiMessage(r));

  /* ---------------- estado de ocupado ---------------- */
  function roots() {
    return $$('[data-sv-cart]');
  }
  function refreshBusy() {
    const busy = S.busy > 0;
    const dirty = S.qty.size > 0;
    roots().forEach((r) => {
      r.toggleAttribute('data-busy', busy);
      r.setAttribute('aria-busy', busy ? 'true' : 'false');
    });
    $$('[data-sv-cta]').forEach((b) => b.setAttribute('aria-disabled', busy || dirty ? 'true' : 'false'));
  }
  function setBusy(on) {
    S.busy += on ? 1 : -1;
    if (S.busy < 0) S.busy = 0;
    refreshBusy();
  }

  /* ---------------- transação com evento do Horizon ---------------- */
  async function resync() {
    await Promise.all(
      sectionComponents().map((c) =>
        sectionRenderer
          .renderSection(c.dataset.sectionId, { cache: false, mode: c.dataset.drawer !== undefined ? 'hydration' : 'full' })
          .catch(() => {})
      )
    );
  }

  /**
   * Dispara CartLinesUpdateEvent, roda `work` e só então resolve a promise do evento (contadores e morph
   * dependem dela). Qualquer falha rejeita a promise e re-renderiza o estado do servidor.
   * work() devolve { cart, sections }.
   */
  async function transaction({ action, lines, anchor, errorKey }, work) {
    const mod = await getEvents();
    const Evt = mod && mod.CartLinesUpdateEvent;
    const deferred = Evt ? Evt.createPromise() : null;
    if (Evt) (anchor && anchor.isConnected ? anchor : doc).dispatchEvent(new Evt({ action, context: 'cart', lines, promise: deferred.promise }));
    setBusy(true);
    try {
      const out = await work();
      if (deferred) {
        deferred.resolve({
          cart: Evt.createCartFromAjaxResponse(out.cart),
          detail: { items: out.cart.items, source: 'sv-cart', sourceId: 'sv-cart', itemCount: out.cart.item_count, sections: out.sections, didError: false },
        });
      }
      SVx().emit && SVx().emit('cart:change', { count: out.cart.item_count, cart: out.cart });
      if (!S.qty.size) paintCount(out.cart.item_count);
      return out;
    } catch (err) {
      if (deferred) deferred.reject(err);
      const msg = err instanceof CartError ? err.userMessage : err && err.name === 'TypeError' ? MSG_NET : MSG_GENERIC;
      S.failed = true;
      if (errorKey) S.pending.set(errorKey, msg);
      await resync();
      hydrate();
      live(msg);
      throw err;
    } finally {
      setBusy(false);
    }
  }
  const enqueue = (fn) => {
    const p = S.queue.then(fn);
    S.queue = p.catch(() => {});
    return p;
  };

  function lineByKey(key) {
    if (String(key).startsWith('v:')) return $$('.sv-line').find((l) => l.dataset.variantId === key.slice(2)) || null;
    return $$('.sv-line').find((l) => l.dataset.key === key) || null;
  }
  function parseJson(s, fb) {
    try {
      return JSON.parse(s);
    } catch (e) {
      return fb;
    }
  }
  function snapshot(line) {
    let props = parseJson(line.dataset.properties, {});
    if (Array.isArray(props)) props = Object.fromEntries(props.filter((x) => Array.isArray(x) && x.length === 2));
    const snap = {
      id: Number(line.dataset.variantId),
      quantity: Number(line.dataset.quantity) || 1,
      properties: props && typeof props === 'object' && Object.keys(props).length ? props : undefined,
      title: line.dataset.title || '',
    };
    if (line.dataset.sellingPlan) snap.selling_plan = Number(line.dataset.sellingPlan);
    return snap;
  }
  const sameProps = (a, b) => JSON.stringify(a || {}) === JSON.stringify(b || {});
  // A chave da linha muda quando a sacola muda (medido na loja): antes de mexer numa linha, relê a chave atual.
  async function currentKey(line) {
    const snap = snapshot(line);
    const c = await fetchCart();
    const planOf = (i) => (i.selling_plan_allocation ? i.selling_plan_allocation.selling_plan.id : null);
    const hit = c.items.find((i) => i.variant_id === snap.id && sameProps(i.properties, snap.properties) && planOf(i) === (snap.selling_plan || null));
    if (!hit) throw new CartError('line_gone', 'Essa peça não está mais na sacola.');
    return hit.key;
  }

  /* ---------------- quantidade ---------------- */
  function bumpQty(line, delta) {
    const key = line.dataset.key;
    const max = Number(line.dataset.max) || 99;
    const st = S.qty.get(key) || { target: Number(line.dataset.quantity) || 1 };
    const next = Math.min(max, Math.max(1, st.target + delta));
    if (next === st.target) return;
    st.target = next;
    clearTimeout(st.timer);
    st.timer = setTimeout(() => flushQty(key), 320);
    S.qty.set(key, st);
    const n = $('[data-sv-qty]', line);
    if (n) n.textContent = String(next);
    const root = line.closest('[data-sv-cart="drawer"]');
    if (root) paintCount($$('.sv-line', root).reduce((sum, l) => sum + (S.qty.has(l.dataset.key) ? S.qty.get(l.dataset.key).target : Number(l.dataset.quantity) || 0), 0));
    const [minus, plus] = $$('[data-sv-act="qty"]', line);
    if (minus) minus.disabled = next <= 1;
    if (plus) plus.disabled = next >= max;
    clearLineError(line);
    refreshBusy();
  }
  function flushQty(key) {
    const st = S.qty.get(key);
    if (!st) return Promise.resolve();
    clearTimeout(st.timer);
    S.qty.delete(key);
    refreshBusy();
    const line = lineByKey(key);
    if (!line || Number(line.dataset.quantity) === st.target) return Promise.resolve();
    return enqueue(() => changeQty(line, st.target)).catch(() => {});
  }
  function flushAllQty() {
    return Promise.all(Array.from(S.qty.keys()).map(flushQty));
  }
  async function changeQty(line, qty) {
    const key = line.dataset.key;
    const out = await transaction({ action: 'update', lines: [{ id: key, quantity: qty }], anchor: line, errorKey: key }, async () => {
      const r = await cartPost('cartChange', { id: await currentKey(line), quantity: qty, sections: sectionIds().join(','), sections_url: location.pathname });
      if (!r.ok) throw apiError(r);
      return { cart: r.json, sections: r.json.sections };
    });
    // a plataforma pode limitar ao estoque sem erro: avisa a diferença
    const got = (out.cart.items.find((i) => i.key === key) || {}).quantity;
    if (typeof got === 'number' && got < qty) {
      S.pending.set('v:' + line.dataset.variantId, `Só conseguimos reservar ${got} ${got === 1 ? 'peça' : 'peças'} desta opção.`);
      hydrate();
    }
  }

  /* ---------------- remover e desfazer ---------------- */
  async function removeLine(line) {
    if (line.hasAttribute('data-removing')) return;
    line.setAttribute('data-removing', '');
    const inDrawer = !!line.closest('#cart-drawer');
    const key = line.dataset.key;
    const snap = snapshot(line);
    S.qty.delete(key);
    try {
      await enqueue(() =>
        transaction({ action: 'remove', lines: [{ id: key, quantity: 0 }], anchor: line, errorKey: key }, async () => {
          const r = await cartPost('cartChange', { id: await currentKey(line), quantity: 0, sections: sectionIds().join(','), sections_url: location.pathname });
          if (!r.ok) throw apiError(r);
          return { cart: r.json, sections: r.json.sections };
        })
      );
      if (inDrawer) {
        // gaveta: aviso curto no lugar do Desfazer (a linha de Desfazer fica escondida; a página /cart mantém)
        toast('Item removido do carrinho');
      } else {
        S.undo = snap;
        S.undoError = '';
        live(`${snap.title} removido da sacola. Use Desfazer remoção para recuperar.`);
        renderUndo();
      }
    } catch (e) {
      line.removeAttribute('data-removing');
    }
  }
  async function undoRemove() {
    const snap = S.undo;
    if (!snap || S.busy) return;
    const slot = $('[data-sv-undo-slot]');
    try {
      await enqueue(() =>
        transaction({ action: 'add', lines: [{ merchandiseId: String(snap.id), quantity: snap.quantity }], anchor: slot }, async () => {
          const item = { id: snap.id, quantity: snap.quantity };
          if (snap.properties) item.properties = snap.properties;
          if (snap.selling_plan) item.selling_plan = snap.selling_plan;
          const r = await cartPost('cartAdd', { items: [item], sections: sectionIds().join(','), sections_url: location.pathname });
          if (!r.ok) throw apiError(r);
          return { cart: await fetchCart(), sections: r.json.sections };
        })
      );
      S.undo = null;
      S.undoError = '';
      live(`${snap.title} voltou para a sacola.`);
    } catch (e) {
      S.undoError = e instanceof CartError ? e.userMessage : e && e.name === 'TypeError' ? MSG_NET : MSG_GENERIC;
      S.undo = null;
    }
    renderUndo();
  }
  function renderUndo() {
    const slots = $$('[data-sv-undo-slot]');
    slots.forEach((slot) => {
      const html = S.undo
        ? `<span class="sv-visually-hidden" role="status">Peça removida.</span><button type="button" class="sv-undo" data-sv-act="undo">Desfazer remoção</button>`
        : S.undoError
          ? `<p class="sv-undo-error" role="alert">${esc(S.undoError)}</p>`
          : '';
      if (slot.innerHTML !== html) slot.innerHTML = html;
    });
  }

  /* ---------------- produto (.js) e trocas de variante ---------------- */
  function ensureProduct(handle) {
    if (!S.products.has(handle)) {
      const p = fetch(`${route('root', '/').replace(/\/?$/, '/')}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null);
      S.products.set(handle, p);
      p.then((v) => v || S.products.delete(handle));
    }
    return S.products.get(handle);
  }
  const optNames = (p) => (p.options || []).map((o) => (typeof o === 'string' ? o : o.name));

  function chosenOptions(line, product) {
    const cur = product.variants.find((v) => String(v.id) === line.dataset.variantId);
    return cur ? cur.options.slice() : null;
  }

  // gaveta: marca a bolinha e troca a foto da linha na hora, sem esperar a rede (o servidor confirma e redesenha em seguida;
  // se falhar, o resync devolve o estado verdadeiro)
  async function previewColor(line, dot) {
    $$('.svc-dot', line).forEach((d) => d.setAttribute('aria-pressed', String(d === dot)));
    const img = $('.sv-line__img', line);
    const pos = Number(line.dataset.colorPos);
    if (!img || !pos) return;
    const product = await ensureProduct(line.dataset.handle);
    const opts = product && line.isConnected ? chosenOptions(line, product) : null;
    if (!opts) return;
    opts[pos - 1] = dot.dataset.svValue;
    const v = product.variants.find((x) => x.options.every((o, i) => o === opts[i])) || product.variants.find((x) => x.options[pos - 1] === dot.dataset.svValue && x.featured_image);
    const src = v && v.featured_image && v.featured_image.src;
    if (!src) return;
    img.src = imgAt(src, 480);
    img.srcset = [240, 360, 480].map((w) => `${imgAt(src, w)} ${w}w`).join(', ');
  }

  async function swapOption(line, kind, value, control) {
    if (S.busy) return resyncUi();
    const key = line.dataset.key;
    const pos = Number(kind === 'color' ? line.dataset.colorPos : line.dataset.sizePos);
    clearLineError(line);
    const product = await ensureProduct(line.dataset.handle);
    if (!product || !pos) {
      S.pending.set(key, MSG_GENERIC);
      return resyncUi();
    }
    const opts = chosenOptions(line, product);
    if (!opts) return resyncUi();
    opts[pos - 1] = value;
    const next = product.variants.find((v) => v.options.every((o, i) => o === opts[i]));
    const fail = (msg) => {
      S.pending.set(key, msg);
      return resyncUi();
    };
    if (!next) return fail('Essa combinação não existe para esta peça.');
    if (String(next.id) === line.dataset.variantId) return resyncUi();
    if (!next.available) return fail('Essa opção está esgotada.');

    const snap = snapshot(line);
    S.focusAfter = { variantId: next.id, kind };
    try {
      await enqueue(() =>
        transaction({ action: 'update', lines: [{ id: key, quantity: snap.quantity }], anchor: line, errorKey: key }, async () => {
          const before = await fetchCart();
          const oldLine = before.items.find((i) => i.key === key);
          if (!oldLine) throw new CartError('line_gone', 'Essa peça não está mais na sacola.');
          const q = oldLine.quantity;
          const existing = before.items.find(
            (i) =>
              i.key !== key &&
              i.variant_id === next.id &&
              sameProps(i.properties, oldLine.properties) &&
              (i.selling_plan_allocation ? i.selling_plan_allocation.selling_plan.id : null) ===
                (oldLine.selling_plan_allocation ? oldLine.selling_plan_allocation.selling_plan.id : null)
          );
          // 1) adiciona a nova ANTES de mexer na antiga (se der erro, a sacola fica intacta)
          const item = { id: next.id, quantity: q };
          if (snap.properties) item.properties = snap.properties;
          if (snap.selling_plan) item.selling_plan = snap.selling_plan;
          const add = await cartPost('cartAdd', { items: [item] });
          if (!add.ok) throw apiError(add);
          // 2) a chave da linha muda quando a sacola muda (medido): relê a sacola e acha a antiga pelo conteúdo
          const planOf = (i) => (i.selling_plan_allocation ? i.selling_plan_allocation.selling_plan.id : null);
          const undo = () => rollbackAdd(next.id, q, existing ? existing.quantity : 0, oldLine, planOf);
          let rm;
          try {
            const mid = await fetchCart();
            const cur = mid.items.find((i) => i.variant_id === oldLine.variant_id && sameProps(i.properties, oldLine.properties) && planOf(i) === planOf(oldLine));
            if (!cur) throw new CartError('line_gone', 'Essa peça não está mais na sacola.');
            rm = await cartPost('cartChange', { id: cur.key, quantity: 0, sections: sectionIds().join(','), sections_url: location.pathname });
          } catch (netErr) {
            await undo();
            throw netErr;
          }
          if (!rm.ok) {
            await undo();
            throw apiError(rm);
          }
          return { cart: rm.json, sections: rm.json.sections };
        })
      );
      live('Opção atualizada.');
    } catch (e) {
      S.focusAfter = null;
    }
  }
  // desfaz a adição da variante nova (chave relida, pois ela muda a cada alteração da sacola)
  async function rollbackAdd(variantId, addedQty, qtyBefore, oldLine, planOf) {
    try {
      const c = await fetchCart();
      const l = c.items.find((i) => i.variant_id === variantId && sameProps(i.properties, oldLine.properties) && planOf(i) === planOf(oldLine));
      if (l) await cartPost('cartChange', { id: l.key, quantity: qtyBefore });
    } catch (e) {
      /* o resync do transaction mostra o estado verdadeiro */
    }
  }
  async function resyncUi() {
    await resync();
    hydrate();
  }

  /* ---------------- erro por linha ---------------- */
  function showLineError(line, msg) {
    const el = $('[data-sv-error]', line);
    if (!el) return;
    el.textContent = msg;
    el.hidden = !msg;
  }
  function clearLineError(line) {
    showLineError(line, '');
    S.pending.delete(line.dataset.key);
  }

  /* ---------------- entrega ---------------- */
  const WD = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
  const MO = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
  function addBusiness(d, n) {
    const x = new Date(d);
    while (n > 0) {
      x.setDate(x.getDate() + 1);
      const w = x.getDay();
      if (w !== 0 && w !== 6) n--;
    }
    return x;
  }
  const fmtDate = (d) => `${WD[d.getDay()]} ${d.getDate()} de ${MO[d.getMonth()]}`;
  // prazo total em dias úteis, da compra à entrega: fonte única SV.ship (assets/sv-core.js)
  function eta() {
    if (SVx().ship) {
      const w = SVx().ship.window(new Date());
      return [w.start, w.end];
    }
    const d = (cfg().shipping && cfg().shipping.days) || [5, 12];
    const now = new Date();
    return [addBusiness(now, d[0]), addBusiness(now, d[1])];
  }
  function fillEta() {
    const els = $$('[data-sv-eta]:not([data-sv-done])');
    if (!els.length) return;
    const [a, b] = eta();
    els.forEach((el) => {
      el.dataset.svDone = '1';
      if (el.dataset.svEta === 'line') el.innerHTML = `Receba <strong>Grátis</strong> entre <b>${esc(fmtDate(a))}</b> e <b>${esc(fmtDate(b))}</b>`;
      else {
        el.innerHTML = `Se você finalizar hoje, você recebe entre <b>${esc(fmtDate(a))}</b> e <b>${esc(fmtDate(b))}</b>.`;
        el.hidden = false;
      }
    });
  }

  /* ---------------- camadas (dialog aninhado) ---------------- */
  function openLayer(dlg, trigger) {
    if (!dlg || dlg.open) return;
    dlg._svTrigger = trigger || doc.activeElement;
    dlg.showModal();
    dlg.addEventListener('close', () => closeLayerFocus(dlg), { once: true });
  }
  function closeLayerFocus(dlg) {
    const t = dlg._svTrigger;
    dlg._svTrigger = null;
    if (t && t.isConnected) t.focus({ preventScroll: true });
  }

  /* ---------------- gaveta: modal também no desktop ---------------- */
  function onDrawerOpen(e) {
    const drawer = e.target;
    if (!drawer || drawer.id !== 'cart-drawer') return;
    const panel = $('dialog', drawer);
    if (panel && panel.open && !panel.matches(':modal') && matchMedia('(min-width: 990px)').matches) {
      panel.close();
      lockScroll(panel);
      panel.showModal();
      removeTrapFocus();
      const first = $('.theme-drawer__close-button', panel);
      if (first) first.focus({ preventScroll: true });
    }
    hydrate();
  }

  /* ---------------- 2ª peça com 15% off (config e conta no topo do arquivo) ---------------- */
  const money = (c) => (SVx().money ? SVx().money(c) : 'R$ ' + (c / 100).toFixed(2).replace('.', ','));
  const pixPct = () => (typeof cfg().pixPct === 'number' ? cfg().pixPct : 5);
  const instOf = (c) => (SVx().installment ? SVx().installment(c) : null);
  const imgAt = (src, w) => (src ? (src.startsWith('//') ? 'https:' + src : src) + (src.includes('?') ? '&' : '?') + 'width=' + w : '');
  function sv2Lines(root) {
    return $$('.sv-line:not(.sv-line--nested)', root).map((el) => ({
      el,
      key: el.dataset.key,
      handle: el.dataset.handle,
      qty: Number(el.dataset.quantity) || 0,
      price: Number(el.dataset.price) || 0,
    }));
  }
  // Roda a cada morph (síncrono no MutationObserver, antes de pintar): idempotente, só escreve o que falta.
  function sv2Apply() {
    if (!SV_2A_ATIVO) return;
    roots().forEach((root) => {
      // gaveta: sem o cartão da oferta, os totais ficam os da plataforma (a Yampi aplica a 2ª peça no checkout)
      if (root.dataset.svCart === 'drawer') return;
      const lines = sv2Lines(root);
      const pick = sv2Pick(lines, SV_2A);
      const sum = $('[data-sv-summary]', root);
      if (!pick) return;
      const line = lines.find((l) => l.key === pick.key);
      if (line && $('.sv-line__strike', line.el)) {
        // gaveta: o riscado já é o preço cheio; só o Pix da linha passa a refletir os 15% (compara antes de escrever: o observer reage a cada escrita)
        const p = pixPct();
        const now = (Number(line.el.dataset.linePrice) || line.price * line.qty) - pick.cents;
        const pixN = $('.sv-line__pix .sv-nowrap', line.el);
        const want = money(p > 0 ? sv2Pix(now, p) : now);
        if (pixN && pixN.textContent !== want) pixN.textContent = want;
      } else if (line && !$('.sv-line__was', line.el)) {
        const full = Number(line.el.dataset.linePrice) || line.price * line.qty;
        const now = full - pick.cents;
        const pixEl = $('.sv-line__pix', line.el);
        const pixN = $('.sv-nowrap', pixEl || line.el);
        if (pixEl && pixN) {
          const p = pixPct();
          pixN.textContent = money(p > 0 ? sv2Pix(now, p) : now);
          pixEl.insertAdjacentHTML('beforebegin', `<div class="sv-line__was"><span class="sv-ticket">${esc(SV_2A.labels.ticket)}</span><s class="sv-was"><span class="visually-hidden">De </span>${money(p > 0 ? sv2Pix(full, p) : full)}</s></div>`);
          const par = $('.sv-line__parcels .sv-nowrap', line.el);
          const i = instOf(now);
          if (par && i) par.textContent = money(i.value);
        }
      }
      const drawerSum = !!(sum && $('.sv-buy', sum)); // gaveta: sem .sv-econ de marca, então cada escrita compara antes
      if (sum && (drawerSum || !$('.sv-econ', sum))) {
        const sub = Number(sum.dataset.total) || 0;
        const p = pixPct();
        const t = sv2Totals(sub, pick.cents, p);
        const tot = $('[data-sv-total-pix]', sum);
        const put = (el, txt) => {
          if (el && el.textContent !== txt) el.textContent = txt;
        };
        if (drawerSum) {
          put(tot, money(t.tot));
          put($('.sv-save b', sum), money(t.save));
        } else {
          if (tot) tot.textContent = money(t.tot);
          const card = $('.sv-summary__card', sum);
          const econ = `<span>Economize <em class="sv-nowrap">${money(t.save)}</em></span><span class="sv-econ">${money(t.second)} na 2ª peça${p > 0 ? ` + ${money(t.pix)} no <em>Pix</em>` : ''}</span>`;
          if (card) card.innerHTML = econ;
          else if (tot) tot.closest('.sv-summary__total').insertAdjacentHTML('afterend', `<div class="sv-summary__card">${econ}</div>`);
        }
        const i = instOf(t.subD);
        const par = $('.sv-summary__parcel-copy em.sv-nowrap, .sv-buy__parcel .sv-parcels em.sv-nowrap', sum);
        if (par && i) put(par, money(i.value));
        const btn = $('.sv-summary__link[data-sv-act="pay"]', sum);
        if (btn) btn.dataset.cents = String(t.subD);
      }
    });
  }

  // Oferta: só sem desconto já aplicado. Mesmo produto (1 unidade) = outras cores dele; Solene = Anaju | Celine.
  S.offer = null;
  function sv2Base(root) {
    const lines = sv2Lines(root);
    if (!SV_2A_ATIVO || !lines.length || sv2Pick(lines, SV_2A)) return null;
    const sizeOf = (l) => {
      const s = $('select[data-sv-opt="size"]', l.el);
      return s ? s.value : '';
    };
    const colorsOf = (h) => lines.filter((l) => l.handle === h).map((l) => $('.svc-dot[aria-pressed="true"]', l.el)).filter(Boolean).map((d) => d.dataset.svValue);
    const solo = lines.find((l) => SV_2A.pair[l.handle]);
    if (solo) return { sig: 'pair|' + solo.handle + '|' + sizeOf(solo), handles: SV_2A.pair[solo.handle], skip: [], size: sizeOf(solo), title: SV_2A.labels.look };
    const same = lines.find((l) => SV_2A.same.includes(l.handle));
    if (same) {
      const skip = colorsOf(same.handle);
      return { sig: 'same|' + same.handle + '|' + skip.join(',') + '|' + sizeOf(same), handles: [same.handle], skip, size: sizeOf(same), title: SV_2A.labels.color };
    }
    return null;
  }
  const colorPos = (p) => optNames(p).findIndex((n) => /^(cor|cores|color|colour)$/i.test(String(n).trim()));
  const sizePos = (p) => optNames(p).findIndex((n) => /^(tamanho|tam|size)$/i.test(String(n).trim()));
  function sv2Restore(box) {
    if (!box.dataset.sv2) return;
    delete box.dataset.sv2;
    delete box.dataset.seed;
    delete box.dataset.ready;
    const title = $('.sv-suggest__title', box);
    if (title && box.dataset.sv2Title) title.textContent = box.dataset.sv2Title;
    $$('.sv-seg', box).forEach((s) => s.remove());
    $('[data-sv-suggest-list]', box).textContent = '';
  }
  // true = a oferta ocupa o lugar da sugestão
  async function sv2Offer(root, box) {
    const base = sv2Base(root);
    if (!base) {
      sv2Restore(box);
      return false;
    }
    if (!S.offer || S.offer.sig !== base.sig) S.offer = { sig: base.sig, base, handle: base.handles[0], color: '', size: base.size };
    const o = S.offer;
    const product = await ensureProduct(o.handle);
    const tpl = $(`template[data-sv-2a-card="${o.handle}"]`, root);
    if (!product || !tpl || S.offer !== o) {
      if (S.offer === o) sv2Restore(box);
      return S.offer !== o;
    }
    const cp = colorPos(product);
    const sp = sizePos(product);
    const colors = cp < 0 ? [] : Array.from(new Set(product.variants.map((v) => v.options[cp]))).filter((c) => !o.base.skip.includes(c));
    const avail = (c) => product.variants.some((v) => v.available && (cp < 0 || v.options[cp] === c));
    if (cp >= 0 && (!o.color || !colors.includes(o.color))) o.color = colors.find(avail) || '';
    if (cp >= 0 && !o.color) {
      sv2Restore(box);
      return false;
    }
    const sizes = sp < 0 ? [] : Array.from(new Set(product.variants.map((v) => v.options[sp])));
    const findV = (size) => product.variants.find((v) => (cp < 0 || v.options[cp] === o.color) && (sp < 0 || v.options[sp] === size));
    if (sp >= 0 && !(findV(o.size) && findV(o.size).available)) o.size = sizes.find((s) => findV(s) && findV(s).available) || sizes[0];
    const key = ['sv2', o.handle, o.color, o.size, o.base.sig].join('|');
    const list = $('[data-sv-suggest-list]', box);
    if (box.dataset.seed === key && $('[data-sv-2a]', list)) return true;

    const frag = tpl.content.cloneNode(true);
    const card = $('.sv-card--cart', frag);
    if (!card) return false;
    card.classList.add('sv-offer');
    const v0 = findV(o.size) || product.variants.find((v) => cp < 0 || v.options[cp] === o.color);
    const full = v0 ? v0.price : product.price;
    const off = sv2Off(full, SV_2A.pct);
    const p = pixPct();
    // foto da cor oferecida
    const img = $('.sv-card__thumb img', card);
    const src = v0 && v0.featured_image && v0.featured_image.src;
    if (img && src) {
      img.src = imgAt(src, 480);
      img.srcset = [240, 360, 480].map((w) => `${imgAt(src, w)} ${w}w`).join(', ');
    }
    // "2ª peça com 15% off" sob o nome (e a nota, se houver)
    let desc = $('.sv-card__desc', card);
    if (!desc) {
      desc = doc.createElement('p');
      desc.className = 'sv-card__desc';
      ($('.sv-card__rating', card) || $('.sv-card__name', card)).insertAdjacentElement('afterend', desc);
    }
    desc.textContent = SV_2A.labels.desc;
    // só as outras cores
    $$('.sv-card__sw li', card).forEach((li) => {
      const d = $('.sv-card__dot', li);
      if (!d || !colors.includes(d.dataset.value)) return li.remove();
      d.setAttribute('aria-pressed', d.dataset.value === o.color ? 'true' : 'false');
      d.dataset.sv2Color = d.dataset.value;
    });
    // preço: Pix cheio riscado, selo 15% OFF, Pix da 2ª peça e a parcela
    const price = $('.sv-price', card);
    if (price) {
      $$('.sv-price__card-line', price).forEach((n) => n.remove());
      let was = $('.sv-was', price);
      const row = $('.sv-price__row', price);
      if (!was && row) {
        row.insertAdjacentHTML('beforebegin', '<s class="sv-was"></s>');
        was = $('.sv-was', price);
      }
      if (was) was.innerHTML = `<span class="visually-hidden">De </span>${money(p > 0 ? sv2Pix(full, p) : full)}`;
      let tk = $('.sv-price__ticket', price);
      if (!tk && row) {
        row.insertAdjacentHTML('afterbegin', '<span></span>');
        tk = row.firstElementChild;
      }
      if (tk) {
        tk.className = 'sv-ticket';
        tk.textContent = SV_2A.labels.ticket;
      }
      const big = $('.sv-price__pix', price);
      if (big) big.textContent = money(p > 0 ? sv2Pix(off, p) : off);
      const i = instOf(off);
      let ins = $('.sv-price__installment', price);
      if (i && !ins) {
        price.insertAdjacentHTML('beforeend', '<p class="sv-price__installment"></p>');
        ins = $('.sv-price__installment', price);
      }
      if (ins) ins.textContent = i ? `em até ${i.n}x${i.interest ? '*' : ''} de ${money(i.value)}` : '';
    }
    // tamanho (o mesmo da peça da sacola) + Adicionar
    const opts = sizes
      .map((s) => {
        const v = findV(s);
        const ok = v && v.available;
        return `<option value="${esc(s)}"${s === o.size ? ' selected' : ''}${ok ? '' : ' disabled'}>${esc(s)}${ok ? '' : ' (esgotado)'}</option>`;
      })
      .join('');
    const ft = `<div class="sv-offer-ft">${sp >= 0 ? `<select data-sv2-size aria-label="Tamanho">${opts}</select>` : ''}<button type="button" class="sv-card__choose" data-sv2-add>${esc(SV_2A.labels.add)}</button></div>`;
    const choose = $('.sv-card__choose, .sv-card__soldout', card);
    if (choose) choose.outerHTML = ft;
    else $('.sv-card__cbody', card).insertAdjacentHTML('beforeend', ft);

    const wrap = doc.createElement('div');
    wrap.className = 'svc-pick';
    wrap.setAttribute('data-sv-2a', o.handle);
    wrap.appendChild(card);
    const title = $('.sv-suggest__title', box);
    if (!box.dataset.sv2Title && title) box.dataset.sv2Title = title.textContent;
    if (title) title.textContent = o.base.title;
    $$('.sv-seg', box).forEach((s) => s.remove());
    if (o.base.handles.length > 1) {
      $('.sv-suggest__head', box).insertAdjacentHTML(
        'beforeend',
        `<div class="sv-seg" role="group" aria-label="${esc(SV_2A.labels.seg)}">${o.base.handles
          .map((h) => `<button type="button" data-sv2-pick="${esc(h)}" aria-pressed="${h === o.handle}">${esc(SV_2A.labels.short[h] || h)}</button>`)
          .join('')}</div>`
      );
    }
    list.replaceChildren(wrap);
    box.dataset.sv2 = '1';
    box.dataset.seed = key;
    box.dataset.ready = '1';
    box.hidden = false;
    return true;
  }
  function sv2Repaint() {
    const root = roots().find((r) => $('[data-sv-suggest]', r) && r.offsetParent !== null) || roots()[0];
    const box = root && $('[data-sv-suggest]', root);
    if (box) sv2Offer(root, box);
  }
  async function sv2Add(btn) {
    const o = S.offer;
    if (!o || S.busy) return;
    const product = await ensureProduct(o.handle);
    if (!product) return;
    const cp = colorPos(product);
    const sp = sizePos(product);
    const v = product.variants.find((x) => (cp < 0 || x.options[cp] === o.color) && (sp < 0 || x.options[sp] === o.size));
    const card = btn.closest('.sv-card');
    if (!v || !v.available) {
      live('Essa opção está esgotada.');
      return;
    }
    btn.disabled = true;
    try {
      await enqueue(() =>
        transaction({ action: 'add', lines: [{ merchandiseId: String(v.id), quantity: 1 }], anchor: card }, async () => {
          const r = await cartPost('cartAdd', { items: [{ id: v.id, quantity: 1 }], sections: sectionIds().join(','), sections_url: location.pathname });
          if (!r.ok) throw apiError(r);
          return { cart: await fetchCart(), sections: r.json.sections };
        })
      );
      live(`${product.title} adicionado à sacola com ${SV_2A.pct}% off.`);
    } catch (e) {
      if (btn.isConnected) btn.disabled = false;
    }
  }
  doc.addEventListener(
    'click',
    (e) => {
      const t = e.target.closest && e.target.closest('[data-sv-2a] .sv-card__dot, [data-sv2-pick], [data-sv2-add]');
      if (!t || !S.offer) return;
      e.preventDefault();
      e.stopPropagation();
      if (t.matches('[data-sv2-add]')) return sv2Add(t);
      if (t.matches('[data-sv2-pick]')) {
        if (S.offer.handle === t.dataset.sv2Pick) return;
        S.offer.handle = t.dataset.sv2Pick;
        S.offer.color = '';
        S.offer.size = S.offer.base.size;
      } else S.offer.color = t.dataset.sv2Color || t.dataset.value;
      sv2Repaint();
    },
    true
  );
  doc.addEventListener('change', (e) => {
    const s = e.target.closest && e.target.closest('[data-sv2-size]');
    if (s && S.offer) S.offer.size = s.value;
  });

  /* ---------------- sugestões ---------------- */
  function seedFor(root) {
    const lines = $$('.sv-line:not(.sv-line--nested)', root);
    const last = lines[lines.length - 1];
    if (last) return Promise.resolve({ id: last.dataset.productId, title: last.dataset.title });
    const h = (SVx().recent && SVx().recent.list()[0]) || null;
    if (!h) return Promise.resolve(null);
    return ensureProduct(h).then((p) => (p ? { id: String(p.id), title: p.title } : null));
  }
  async function loadSuggestion(seed, intent) {
    const url = `${route('root', '/').replace(/\/?$/, '/')}recommendations/products?section_id=cart-drawer-section&product_id=${encodeURIComponent(seed.id)}&limit=4&intent=${intent}`;
    const res = await fetch(url, { headers: { Accept: 'text/html' } });
    if (!res.ok) return null;
    const html = await res.text();
    const node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-sv-suggest-result]');
    return node && node.querySelector('.svc-pick') ? node.innerHTML : null;
  }
  async function ensureSuggest() {
    const root = roots().find((r) => $('[data-sv-suggest]', r) && r.offsetParent !== null) || roots()[0];
    // gaveta enxuta: sem sugestão e sem 2ª peça (nem a busca de recomendações); a página /cart mantém
    // 17/10/2026: sugestões pausadas também na página /cart (sem order bump por alguns dias); voltar a true para religar
    if (!SV_SUGGEST_ATIVO) return;
    if (!root || root.dataset.svCart === 'drawer') return;
    const box = $('[data-sv-suggest]', root);
    if (!box) return;
    // 2ª peça com 15% off: o card da oferta ocupa o lugar da sugestão
    if (await sv2Offer(root, box)) return;
    const seed = await seedFor(root);
    if (!seed) {
      box.hidden = true;
      return;
    }
    const key = String(seed.id);
    const list = $('[data-sv-suggest-list]', box);
    // o morph do carrinho apaga os filhos da lista: só sai cedo se ela ainda estiver preenchida
    if (box.dataset.seed === key && box.dataset.ready && list.childElementCount) return;
    box.dataset.seed = key;
    delete box.dataset.ready;
    const paint = (html, intent) => {
      if (box.dataset.seed !== key) return;
      list.innerHTML = html;
      // sem frase de motivo sob o título (pedido do dono): o card vem logo após "Seu próximo look"
      $$('[data-sv-reason]', list).forEach((p) => p.remove());
      box.hidden = false;
      box.dataset.ready = '1';
    };
    const hide = () => {
      if (box.dataset.seed !== key) return;
      box.hidden = true;
      box.dataset.ready = '1';
    };
    let result = S.suggest.get(key);
    if (!result) {
      // uma requisição por semente, mesmo com várias chamadas seguidas (in-flight compartilhado)
      let job = S.suggestJobs.get(key);
      if (!job) {
        job = (async () => {
          let intent = 'complementary';
          let html = await loadSuggestion(seed, intent);
          if (!html) {
            intent = 'related';
            html = await loadSuggestion(seed, intent);
          }
          const r = { html, intent };
          S.suggest.set(key, r); // html null = sem sugestão (não busca de novo)
          return r;
        })();
        S.suggestJobs.set(key, job);
        job.finally(() => S.suggestJobs.delete(key)).catch(() => {});
      }
      try {
        result = await job;
      } catch (e) {
        box.hidden = true;
        return;
      }
    }
    if (result.html) paint(result.html, result.intent);
    else hide();
  }

  /* ---------------- disponibilidade de cor/tamanho por linha ---------------- */
  async function enhanceLine(line) {
    if (!line.dataset.handle || (!Number(line.dataset.colorPos) && !Number(line.dataset.sizePos))) return;
    const product = await ensureProduct(line.dataset.handle);
    if (!product || !line.isConnected) return;
    const opts = chosenOptions(line, product);
    if (!opts) return;
    const cp = Number(line.dataset.colorPos);
    const sp = Number(line.dataset.sizePos);
    const find = (o) => product.variants.find((v) => v.options.every((x, i) => x === o[i]));
    $$('.svc-dot', line).forEach((d) => {
      const o = opts.slice();
      o[cp - 1] = d.dataset.svValue;
      const v = find(o);
      const off = !v || !v.available;
      if (d.toggleAttribute) {
        if (d.hasAttribute('aria-disabled') !== off) d.toggleAttribute('aria-disabled', off);
      }
      if (off && sp) d.title = `${d.dataset.svValue}: indisponível no tamanho ${opts[sp - 1]}`;
    });
    const sel = $('select[data-sv-opt="size"]', line);
    if (sel && sp) {
      $$('option', sel).forEach((opt) => {
        const o = opts.slice();
        o[sp - 1] = opt.value;
        const v = find(o);
        const off = !v || !v.available;
        if (opt.disabled !== off) opt.disabled = off;
        const base = opt.value;
        const want = off ? `${base} (esgotado)` : base;
        if (opt.textContent !== want) opt.textContent = want;
      });
    }
  }

  /* ---------------- hidratação pós-render ---------------- */
  let hydrateTimer = 0;
  function hydrate() {
    clearTimeout(hydrateTimer);
    hydrateTimer = setTimeout(runHydrate, 40);
  }
  function runHydrate() {
    if (!roots().length) return;
    sv2Apply();
    fillEta();
    renderUndo();
    toastEl();
    refreshBusy();
    S.pending.forEach((msg, key) => {
      const line = lineByKey(key);
      if (line) {
        showLineError(line, msg);
        S.pending.delete(key);
      }
    });
    if (S.focusAfter) {
      const { variantId, kind } = S.focusAfter;
      const line = $$('.sv-line').find((l) => l.dataset.variantId === String(variantId));
      if (line) {
        S.focusAfter = null;
        const target = kind === 'size' ? $('select[data-sv-opt="size"]', line) : $('.svc-dot[aria-pressed="true"]', line);
        if (target) target.focus({ preventScroll: false });
      }
    }
    const visible = $('#cart-drawer[open]') || $('[data-sv-cart="page"]');
    if (visible) {
      $$('.sv-line').forEach(enhanceLine);
      ensureSuggest();
    }
  }
  const mo = new MutationObserver((muts) => {
    if (muts.some((m) => m.target.closest && m.target.closest('[data-sv-cart], cart-items-component, cart-drawer-component'))) {
      sv2Apply(); // síncrono: o preço da 2ª peça entra antes de a tela pintar o morph
      hydrate();
    }
  });
  mo.observe(doc.body, { childList: true, subtree: true });

  /* ---------------- eventos ---------------- */
  doc.addEventListener('click', (e) => {
    const t = e.target.closest && e.target.closest('[data-sv-act]');
    if (!t || t.tagName === 'SELECT') return;
    const act = t.dataset.svAct;
    const line = t.closest('.sv-line');
    if (act === 'qty' && line) {
      if (S.busy) return;
      bumpQty(line, Number(t.dataset.delta) || 0);
    } else if (act === 'remove' && line) {
      if (S.busy) return;
      removeLine(line);
    } else if (act === 'opt' && line) {
      if (t.getAttribute('aria-pressed') === 'true') return;
      if (t.hasAttribute('aria-disabled')) {
        showLineError(line, t.title || 'Opção indisponível.');
        return;
      }
      if (t.dataset.svOpt === 'color' && !S.busy && line.closest('#cart-drawer')) previewColor(line, t);
      swapOption(line, t.dataset.svOpt, t.dataset.svValue, t);
    } else if (act === 'undo') {
      undoRemove();
    } else if (act === 'delivery') {
      openLayer($('.sv-sheetd', t.closest('[data-sv-lines-wrap]') || doc), t);
    } else if (act === 'layer-close') {
      const d = t.closest('dialog');
      if (d) d.close();
    } else if (act === 'pay') {
      SVx().emit && SVx().emit('pay:open', { cents: Number(t.dataset.cents) || 0, source: 'cart', trigger: t });
    }
  });
  doc.addEventListener('change', (e) => {
    const sel = e.target.closest && e.target.closest('select[data-sv-act="opt"]');
    if (!sel) return;
    const line = sel.closest('.sv-line');
    if (line) swapOption(line, sel.dataset.svOpt, sel.value, sel);
  });
  // clique no fundo da folha de entrega fecha
  doc.addEventListener('click', (e) => {
    if (e.target instanceof HTMLDialogElement && e.target.classList.contains('sv-sheetd')) e.target.close();
  });
  // sugestão da sacola: "Escolher tamanho" abre o mini-PDP dentro da sacola (não a compra rápida da coleção)
  doc.addEventListener(
    'click',
    (e) => {
      const q = e.target.closest && e.target.closest('[data-sv-suggest] [data-sv-quick], [data-sv-suggest] [data-sv-pick]');
      if (!q) return;
      e.preventDefault();
      e.stopPropagation();
      SVx().emit && SVx().emit('minipdp:open', { handle: q.dataset.svQuick || q.dataset.svPick, trigger: q, from: 'cart' });
    },
    true
  );
  // Finalizar: espera alterações pendentes (e não segue se alguma falhou) antes de chamar a Yampi, que lê /cart.json.
  // yampiClick() não trata erro e deixa o loader branco: se a navegação não acontecer em 12 s, esconde o loader e avisa.
  function ctaNotice(cta, msg) {
    let n = cta.parentElement.querySelector('.sv-cta-error');
    if (!n) {
      n = doc.createElement('p');
      n.className = 'sv-cta-error';
      n.setAttribute('role', 'alert');
      cta.insertAdjacentElement('afterend', n);
    }
    n.textContent = msg;
    n.hidden = !msg;
  }
  doc.addEventListener(
    'click',
    async (e) => {
      const cta = e.target.closest && e.target.closest('[data-sv-cta]');
      if (!cta || cta.__svRetry) return;
      // toques repetidos enquanto a Yampi abre: cada um postava a sacola de novo (Clarity 20/10/2026). Só o 1º segue.
      if (S.yampiTimer) { e.preventDefault(); e.stopImmediatePropagation(); return; }
      ctaNotice(cta, '');
      if (S.busy || S.qty.size) {
        e.preventDefault();
        e.stopImmediatePropagation();
        S.failed = false;
        await flushAllQty();
        await S.queue;
        if (S.failed || S.pending.size) {
          ctaNotice(cta, 'Não conseguimos atualizar a sacola. Confira os avisos nas peças e tente de novo.');
          return;
        }
        if (cta.isConnected && !S.busy) {
          cta.__svRetry = true;
          armYampiTimeout(cta);
          cta.click();
          cta.__svRetry = false;
        }
        return;
      }
      armYampiTimeout(cta);
    },
    true
  );
  function armYampiTimeout(cta) {
    clearTimeout(S.yampiTimer);
    S.yampiTimer = setTimeout(() => {
      S.yampiTimer = 0;
      if (typeof window.hideYampiLoader === 'function') window.hideYampiLoader();
      if (!cta.isConnected) return;
      ctaNotice(cta, 'Não foi possível abrir o pagamento agora. Toque de novo em instantes.');
      const wa = SVx().config?.contact?.waLink;
      const n = wa && cta.parentElement.querySelector('.sv-cta-error');
      if (n) n.insertAdjacentHTML('beforeend', ' Se continuar, <a href="' + wa + '" target="_blank" rel="noopener">fale com a gente no WhatsApp</a>.');
    }, 12000);
  }
  window.addEventListener('pagehide', () => { clearTimeout(S.yampiTimer); S.yampiTimer = 0; });
  // voltar do checkout pelo botão do navegador (página do cache): libera o botão
  window.addEventListener('pageshow', (e) => { if (e.persisted) { clearTimeout(S.yampiTimer); S.yampiTimer = 0; window.hideYampiLoader?.(); } });
  // Esc: com uma camada aberta acima da gaveta, fecha só a camada (o <dialog> fecha sozinho)
  window.addEventListener(
    'keydown',
    (e) => {
      if (e.key !== 'Escape') return;
      const drawer = $('#cart-drawer[open]');
      if (!drawer) return;
      const panel = $('dialog', drawer);
      if ($$('dialog[open]').some((d) => d !== panel)) e.stopPropagation();
    },
    true
  );
  /* Rede de segurança: adições fora do Horizon (SV.cart.add da mini-PDP, arara, almoço, pins) às vezes não
     disparam o CartLinesUpdateEvent (o import de @shopify/events falha no script clássico do núcleo; medido em
     14/10). Se a contagem real diverge do que a sacola mostra, re-renderiza a seção e abre a gaveta ao crescer. */
  let syncTimer = 0;
  const shownCount = () => {
    const c = $('#cart-drawer [ref="cartItemCount"]') || $('[data-sv-cart="page"] [ref="cartItemCount"]');
    return c ? parseInt(c.textContent, 10) || 0 : 0;
  };
  doc.addEventListener('sv:cart:change', (e) => {
    const cart = e.detail && e.detail.cart;
    if (!cart || typeof cart.item_count !== 'number') return;
    if (!S.qty.size) paintCount(cart.item_count);
    clearTimeout(syncTimer);
    syncTimer = setTimeout(async () => {
      if (S.busy) return;
      const before = shownCount();
      if (before === cart.item_count) return;
      await resync();
      const drawer = $('#cart-drawer');
      const auto = $('#cart-drawer cart-drawer-component[auto-open]');
      const otherModal = $$('dialog:modal').some((d) => !d.closest('#cart-drawer'));
      // adição na PDP (product-form.js marca a janela): confirma no botão/toast, não abre a gaveta
      const pdpAdd = Date.now() < (window.__svNoOpenUntil || 0);
      if (!pdpAdd && cart.item_count > before && drawer && auto && !otherModal && typeof drawer.open === 'function' && !$('#cart-drawer dialog[open]')) drawer.open();
    }, 450);
  });

  doc.addEventListener('theme-drawer:open', onDrawerOpen);
  doc.addEventListener('theme-drawer:close', () => {
    $$('dialog.sv-sheetd[open]').forEach((d) => d.close());
  });
  // idioma/modo trocados não exigem nada aqui; página restaurada do bfcache re-hidrata
  window.addEventListener('pageshow', hydrate);
  doc.addEventListener('shopify:section:load', hydrate);

  hydrate();
}
